import {
  CustomizationOptions,
  View,
  Widget,
} from '@ally/data-types-customization'
import { current, Draft, isDraft } from 'immer'
// TODO idk what to do here we need this time but they don't export it in the
// eslint-disable-next-line import/extensions, import/no-unresolved
import { WritableDraft } from 'immer/dist/types/types-external'
import { cloneDeep } from 'lodash-es'
import { mergeDeepRight, isEmpty, equals } from 'ramda'
import {
  CustomizeApiAlertProps,
  CustomizeDataObj,
  DashboardView,
  DataTypeName,
  WidgetConfigObj,
} from '../types'
import { reorderWidgets } from '../utils'
import { defaultCustomizeApiAlert } from './CustomizeDashboardContext'
import { filterDashboardView } from './utils'

export type CustomizeAction =
  | {
      type: 'set dashboardView'
      payload: DashboardView
    }
  | {
      type: 'CustomizeDashboard API alert'
      payload: CustomizeApiAlertProps
    }
  | {
      type: 'RESET CustomizeDashboard API alert'
    }
  | {
      type: 'set isCustomizeModalOpen'
      payload: boolean
    }
  | {
      type: 'Add items to customizeDataToPatch'
      payload: Partial<CustomizeDataObj>
    }
  | {
      type: 'Delete Widget'
      payload: string
    }
  | {
      type: 'Add Widgets'
      payload: string[]
    }
  | {
      type: 'set customizeApiData'
      payload:
        | CustomizeDataObj
        | (Partial<CustomizeDataObj> & { customizationId?: string })
    }
  | {
      type: 'set isSaving'
      payload: boolean
    }
  | {
      type: 'set isCustomizationEnabled'
      payload: boolean
    }
  | {
      type: 'set widgets'
      payload: Widget[]
    }

export type CustomizeDispatch = (action: CustomizeAction) => void

export type CustomizeState = {
  /** Customize data from the `/customizations` endpoint */
  customizeApiData: CustomizationOptions | CustomizeDataObj
  /** The view the user sees on `/dashboard` */
  dashboardView: DashboardView
  /** Alert object used for errors and success messages */
  customizeApiAlert: CustomizeApiAlertProps
  /** Is the customize modal open */
  isCustomizeModalOpen: boolean
  /** Is the FF FF_snapshot-customization on and no error */
  isCustomizationEnabled: boolean
  /** All widgets that the user can add to their dashboard */
  eligibleWidgets: WidgetConfigObj
  /** Widgets the user has currently added to their dashboard */
  widgets: Widget[]
  /**
   * This is the main object to determine what customize data the user has changed so that we
   * can send a PATCH API call and easily pass this object instead of diffing like the old version.
   */
  customizeDataToPatch: Partial<CustomizeDataObj>
  /** Determines if the user has made changes and has the ability to save. */
  hasCustomizeChangesToSave: boolean
  /** Is the save `/customizations` API call in progress */
  isSaving: boolean
}

export type DraftCustomizeState = Draft<CustomizeState>

type ExtractActualTypeFromDraft<T> = T extends Draft<infer P>
  ? P extends WritableDraft<infer PType>
    ? PType
    : P
  : never

type DraftObjectOrArray = Draft<Record<string, unknown>> | Draft<any[]>

function immerDeproxify<T extends DraftObjectOrArray>(
  item: T,
): ExtractActualTypeFromDraft<T> {
  return isDraft(item)
    ? (cloneDeep(current(item)) as ExtractActualTypeFromDraft<T>)
    : // istanbul ignore next
      (cloneDeep(item) as ExtractActualTypeFromDraft<T>)
}

function resetCustomizeApiAlert(draftState: DraftCustomizeState): void {
  draftState.customizeApiAlert = defaultCustomizeApiAlert
}

/** This function calculates what customization data the user has changed from their
 * original customizeApiData
 */
function determineCustomizeDataToPatch(
  draftState: DraftCustomizeState,
  payload: Partial<CustomizeDataObj>,
): void {
  // merge existing customizeDataToPatch with the payload, giving the payload priority
  // ! mergeDeepRight DOES NOT MERGE ARRAYS, IT OVERWRITES THEM, Ex: widgets, views
  const mergedObj = mergeDeepRight(draftState.customizeDataToPatch, payload)

  Object.keys(mergedObj).forEach(k => {
    // casted key for TS
    const key = k as DataTypeName
    /**
     * Check to see if the 'mergedObj' data is the same as the original API data.
     * If so, then remove the key. No changes need to be saved.
     */
    if (equals(draftState.customizeApiData[key], mergedObj[key])) {
      delete mergedObj[key]
    }
  })

  draftState.customizeDataToPatch = mergedObj
  draftState.hasCustomizeChangesToSave = !isEmpty(
    draftState.customizeDataToPatch,
  )
}

/** Updates only the dashboard view in the customization API object */
function updateApiDashboardViewValue(
  draftState: DraftCustomizeState,
  value: 'DEFAULT' | 'TILE' = 'DEFAULT',
): View[] {
  return [
    // convert the existing views array that is an immer proxy into a JS object, filter out the existing DASHBOARD VIEW
    ...current(draftState.customizeApiData.views).filter(filterDashboardView),
    { name: 'DASHBOARD', value },
  ]
}

function updateWidgetsAndView(
  draftState: DraftCustomizeState,
  widgets: Widget[],
  views: View[] | null,
): void {
  determineCustomizeDataToPatch(draftState, {
    widgets: reorderWidgets(widgets),
    ...(views?.length ? { views } : {}),
  })
}

function handleAddWidgets(
  draftState: DraftCustomizeState,
  widgetNames: string[],
): void {
  const currentWidgets = immerDeproxify(
    draftState.customizeDataToPatch.widgets ?? draftState.widgets,
  )

  let updatedViews: View[] | null = null

  /**
   * If the user has the TILE view and decides to delete all widgets in the modal and then try to add one back,
   * we need to change the view back to TILE since that's what they had before.
   */
  const allWidgetsAreDisabled = !currentWidgets.some(w => w.enabled)
  if (
    allWidgetsAreDisabled &&
    draftState.customizeApiData.views.find(v => v.name === 'DASHBOARD')
      ?.value === 'TILE'
  ) {
    updatedViews = updateApiDashboardViewValue(draftState, 'TILE')
  }

  // flip the enabled prop to true
  widgetNames.forEach(name => {
    let widget = currentWidgets.find(w => w.name === name)
    // istanbul ignore next
    if (widget) {
      widget.enabled = true
    }
    // this will only happen for legacy widgets
    else {
      widget = {
        name,
        // arbitrary order, it gets reordered later
        order: 77,
        enabled: true,
      }
      currentWidgets.push(widget)
    }
  })

  updateWidgetsAndView(draftState, currentWidgets, updatedViews)
}

function handleDeleteWidget(
  draftState: DraftCustomizeState,
  widgetName: string,
): void {
  const currentWidgets = immerDeproxify(
    draftState.customizeDataToPatch.widgets ?? draftState.widgets,
  )

  let updatedViews: View[] = []
  const widgetToDelete = currentWidgets.find(w => w.name === widgetName)

  // istanbul ignore next
  if (widgetToDelete) {
    widgetToDelete.enabled = false
  }

  const allWidgetsAreDisabled = !currentWidgets.some(w => w.enabled)

  if (allWidgetsAreDisabled) {
    updatedViews = updateApiDashboardViewValue(draftState)
  }

  updateWidgetsAndView(draftState, currentWidgets, updatedViews)
}

type ExhuastivenessMessage<T> = T extends never
  ? never
  : 'Unhandled CustomizeAction type. Add the case in the switch statement above.'

/** TS exhaustiveness check to remind you to add new action type cases */
/* istanbul ignore next */
function confirmImpossibleState<T>(type: ExhuastivenessMessage<T>): void {
  // error is in TS
}

export default function customizeDashboardImmerReducer(
  /** Immer draft state */
  draftState: DraftCustomizeState,
  action: CustomizeAction,
): void {
  switch (action.type) {
    case 'set customizeApiData': {
      const newData = mergeDeepRight(
        draftState.customizeApiData,
        action.payload,
      )

      draftState.customizeApiData = newData
      if (action.payload.widgets) {
        draftState.widgets = newData.widgets
      }
      draftState.hasCustomizeChangesToSave = false
      draftState.customizeDataToPatch = {}

      // rare case that can occur when the user is still missing the enabled prop (legacy) and decides to customize other items without touching the widgets
      const aWidgetHasNoEnabledProp =
        draftState.widgets.length === 0 ||
        draftState.widgets.some(w => w.enabled === undefined)

      const shouldResetDashboardView = (): boolean => {
        // very special case
        if (aWidgetHasNoEnabledProp && draftState.widgets.length === 0) {
          return false
        }
        return draftState.widgets.filter(w => w.enabled).length === 0
      }

      if (shouldResetDashboardView()) {
        draftState.dashboardView = DashboardView.DEFAULT
      }
      break
    }
    case 'set dashboardView':
      draftState.dashboardView = action.payload
      break
    case 'RESET CustomizeDashboard API alert':
      resetCustomizeApiAlert(draftState)
      break
    case 'CustomizeDashboard API alert':
      draftState.customizeApiAlert = action.payload
      break
    case 'set isCustomizeModalOpen':
      draftState.isCustomizeModalOpen = action.payload
      draftState.customizeDataToPatch = {}
      draftState.hasCustomizeChangesToSave = false
      resetCustomizeApiAlert(draftState)
      break
    case 'Add items to customizeDataToPatch':
      resetCustomizeApiAlert(draftState)
      determineCustomizeDataToPatch(draftState, action.payload)
      break
    case 'Delete Widget':
      handleDeleteWidget(draftState, action.payload)
      break
    case 'Add Widgets':
      handleAddWidgets(draftState, action.payload)
      break
    case 'set isSaving':
      draftState.isSaving = action.payload
      break
    case 'set isCustomizationEnabled':
      draftState.isCustomizationEnabled = action.payload
      break
    case 'set widgets': {
      draftState.widgets = action.payload
      break
    }
    /* istanbul ignore next */
    default:
      confirmImpossibleState(action)
  }
}
